/**
 * Stored commission payouts — the READ side of Books Ship 2b (Sep 25, 2026).
 *
 * Since 2026-09-25 every mark-paid click stores what was actually paid, per
 * (deal, member, line type), in public.commission_payments. Only the definer
 * RPCs write it (commission_mark_paid / rs_pool_mark_paid /
 * commission_trueup_mark_paid; commission_unpay voids). Before that instant the
 * only record of a payout was the boolean flag on the deal, so a paid deal's
 * dollars could only be RECOMPUTED from today's roster, rates and TL/ASM
 * attribution — which is exactly how the Sep 15 roster change silently
 * re-attributed three already-paid deals on the Paid tab.
 *
 * This module is the one seam every paid-money surface reads through:
 *   • a deal paid at/after COMMISSION_PAYMENTS_SINCE renders its STORED lines
 *     (frozen at the click; a payee who has since left the roster still shows);
 *   • a deal paid before it keeps the live recompute, flagged `legacyPaid` so
 *     the UI can say "computed, not recorded";
 *   • pending / approved deals are untouched — nothing has been paid on them.
 *
 * Rows are RLS-scoped (finance tier = all; TL/ASM = self + downline; everyone
 * else = self), so a viewer only ever substitutes the lines they may see. The
 * grouping engine that consumes these rows is groupByPersonStored() next to
 * groupByPerson() in CommissionBoard.tsx.
 */
import { supabase } from "./supabase";
import type { TeamMember } from "./types";
import type { CommFee, CommLineItem, CommType, CommissionDeal } from "../components/commissions/CommissionBoard";

/**
 * The instant commission_mark_paid went live (portal commit 4bd20b4, live on CI
 * within the minute; no commission, RS-pool or true-up payout was stamped on
 * Sep 25 before it — verified). A payout stamped at/after this has stored lines;
 * one stamped before it has only the boolean flags and must be recomputed.
 */
export const COMMISSION_PAYMENTS_SINCE = "2026-09-25T18:17:43Z";
const SINCE_MS = Date.parse(COMMISSION_PAYMENTS_SINCE);

export type PaymentLineType = CommType | "rs_pool";

/** Lines that belong on the sales (RM / TL / ASM) side of the board — the trade upside pool (Oct 29, 2026) too:
 *  a stored trade_upside row left out here would vanish from the Paid tab and read as an RS-pool row elsewhere. */
export const SALES_LINE_TYPES: ReadonlySet<string> = new Set(["rm", "rm_split", "trade_upside", "tl_override", "asm_override", "trueup"]);

export interface CommissionPaymentRow {
  id: string;
  deal_id: string;
  member_id: string;
  line_type: PaymentLineType;
  /** NET amount paid to the member on this line. */
  amount: number;
  gross: number | null;
  rate: number | null;
  rev: number | null;
  /** RS pool rows only — the member's share of the pool at the click. */
  share: number | null;
  fees: CommFee[];
  paid_at: string;
  paid_by: string | null;
  batch_id: string;
  source: string | null;
}

export const COMMISSION_PAYMENT_SELECT =
  "id,deal_id,member_id,line_type,amount,gross,rate,rev,share,fees,paid_at,paid_by,batch_id,source";

export interface StoredPayments {
  byDeal: Map<string, CommissionPaymentRow[]>;
  count: number;
}

export const EMPTY_STORED: StoredPayments = { byDeal: new Map(), count: 0 };

function numOrNull(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// PostgREST returns numerics as STRINGS ("150.75") — normalize every money
// field on the way in so no reader ever does arithmetic on a string.
function normalizeRow(raw: Record<string, unknown>): CommissionPaymentRow {
  const feesRaw = raw.fees;
  const fees: CommFee[] = Array.isArray(feesRaw)
    ? feesRaw.map((f) => {
        const o = (f ?? {}) as Record<string, unknown>;
        return { label: String(o.label ?? ""), amount: Number(o.amount ?? 0) || 0 };
      })
    : [];
  return {
    id: String(raw.id),
    deal_id: String(raw.deal_id),
    member_id: String(raw.member_id),
    line_type: String(raw.line_type) as PaymentLineType,
    amount: Number(raw.amount ?? 0) || 0,
    gross: numOrNull(raw.gross),
    rate: numOrNull(raw.rate),
    rev: numOrNull(raw.rev),
    share: numOrNull(raw.share),
    fees,
    paid_at: String(raw.paid_at),
    paid_by: raw.paid_by == null ? null : String(raw.paid_by),
    batch_id: String(raw.batch_id),
    source: raw.source == null ? null : String(raw.source),
  };
}

const PAGE = 1000;
const IN_CHUNK = 200;

async function fetchPaged(dealIds?: string[]): Promise<CommissionPaymentRow[]> {
  const out: CommissionPaymentRow[] = [];
  for (let from = 0; ; from += PAGE) {
    let q = supabase
      .from("commission_payments")
      .select(COMMISSION_PAYMENT_SELECT)
      .is("voided_at", null)
      .order("id", { ascending: true });
    if (dealIds) q = q.in("deal_id", dealIds);
    const { data, error } = await q.range(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Record<string, unknown>[];
    for (const r of rows) out.push(normalizeRow(r));
    if (rows.length < PAGE) break;
    if (from > 200_000) break; // runaway guard
  }
  return out;
}

/**
 * Live (non-voided) payout rows the viewer may see. Paged past the PostgREST
 * row cap; `dealIds` (optional) is chunked so a long list never overflows the
 * URL. THROWS on error — a money surface must fail loudly, never fall back to
 * a silent recompute (the silent-empty-money bug class).
 */
export async function fetchCommissionPayments(opts: { dealIds?: string[] } = {}): Promise<CommissionPaymentRow[]> {
  if (!opts.dealIds) return fetchPaged();
  const ids = [...new Set(opts.dealIds)];
  if (ids.length === 0) return [];
  const out: CommissionPaymentRow[] = [];
  for (let i = 0; i < ids.length; i += IN_CHUNK) {
    out.push(...(await fetchPaged(ids.slice(i, i + IN_CHUNK))));
  }
  return out;
}

export function indexPayments(rows: CommissionPaymentRow[]): StoredPayments {
  const byDeal = new Map<string, CommissionPaymentRow[]>();
  for (const r of rows) {
    const arr = byDeal.get(r.deal_id);
    if (arr) arr.push(r);
    else byDeal.set(r.deal_id, [r]);
  }
  return { byDeal, count: rows.length };
}

/** True when a payout timestamp falls in the era where the ledger holds it. */
export function isStoredEra(ts: string | null | undefined): boolean {
  if (!ts) return false;
  const ms = Date.parse(String(ts));
  return Number.isFinite(ms) && ms >= SINCE_MS;
}

/** The RM/TL/ASM side of this deal was paid through the ledger. */
export function salesPaidFromStore(d: Pick<CommissionDeal, "commission_paid" | "commission_paid_at">): boolean {
  return !!d.commission_paid && isStoredEra(d.commission_paid_at);
}

/** The RS pool on this deal was paid through the ledger. */
export function rsPoolPaidFromStore(d: Pick<CommissionDeal, "rs_pool_paid" | "rs_pool_paid_at">): boolean {
  return !!d.rs_pool_paid && isStoredEra(d.rs_pool_paid_at);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** A stored sales-side row as the CommLineItem every board renders. */
export function lineFromRow(r: CommissionPaymentRow, deal: CommissionDeal): CommLineItem {
  const amount = round2(r.amount);
  return {
    type: r.line_type as CommType,
    deal,
    amount,
    rev: r.rev ?? 0,
    rate: r.rate ?? 0,
    gross: r.gross ?? amount,
    fees: r.fees,
    stored: true,
    paidAt: r.paid_at,
  };
}

export interface RsPoolLine {
  memberId: string;
  share: number;
  amount: number;
  stored: boolean;
  paidAt: string | null;
}

/**
 * The RS pool lines for a deal paid through the ledger, or null when the pool
 * on this deal was paid before the ledger existed (caller computes live from
 * the current pool composition, as before).
 */
export function storedRsPoolLines(d: CommissionDeal, stored: StoredPayments): RsPoolLine[] | null {
  if (!rsPoolPaidFromStore(d)) return null;
  return (stored.byDeal.get(d.id) ?? [])
    .filter((r) => r.line_type === "rs_pool")
    .map((r) => ({ memberId: r.member_id, share: r.share ?? 0, amount: round2(r.amount), stored: true, paidAt: r.paid_at }));
}

/** Payees in the ledger who aren't in the caller's (active-only) members map. */
export function payeeIdsNotIn(stored: StoredPayments, members: Record<string, TeamMember>): string[] {
  const out = new Set<string>();
  for (const rows of stored.byDeal.values()) {
    for (const r of rows) if (!members[r.member_id]) out.add(r.member_id);
  }
  return [...out];
}

/** Names for payees who have since left the roster (team_roster is staff-readable). */
export async function fetchMemberNames(ids: Iterable<string>): Promise<Record<string, string>> {
  const list = [...new Set(ids)];
  if (list.length === 0) return {};
  const { data, error } = await supabase.from("team_roster").select("id, full_name").in("id", list);
  if (error) throw new Error(error.message);
  const out: Record<string, string> = {};
  for (const r of (data ?? []) as Array<{ id: string; full_name: string | null }>) out[r.id] = r.full_name ?? "";
  return out;
}

/** Resolves a payee's display name: roster first, then the fallback lookup. */
export function payeeName(
  id: string,
  members: Record<string, TeamMember>,
  payeeNames: Record<string, string> = {},
): string {
  return members[id]?.full_name || payeeNames[id] || "Former team member";
}
